/**
 * @module Components.StripeForm
 *
 */
import React, { useState, FormEvent, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { injectStripe } from "react-stripe-elements";
import { MessageDescriptor } from "react-intl";
import { cls } from "app/utils";
import { useActions, useError, usePending } from "app/utils/hooks";
import { Token, ElementChangeResponse } from "app/utils/stripe";
import { Spinner } from "app/components/Loading";
import CardName from "./_components/CardName";
import CardNumber from "./_components/CardNumber";
import CardExpiry from "./_components/CardExpiry";
import CardCVC from "./_components/CardCVC";
import PostalCode from "./_components/PostalCode";
import styles from "./styles.module.scss";
import AddressLineOne from "./_components/AddressLineOne";
import AddressLinetwo from "./_components/AddressLineTwo";
import City from "./_components/City";
import Country from "./_components/Country";
import UsState from "../usState";
import { useTranslation } from "react-i18next";
import FormItem from "app/components/inputs/FormItem";
import { getDiscountedAmount } from "app/utils/planPricing";
import ButtonVariant from "app/components/Button/ButtonVariant";
import PaymentMethodSelector from "app/components/StripeForm/PaymentMethodSelector";
import { useSavedCard } from "app/hooks/useSavedCard";
import { purchaseSubscription, updateSubscription } from "app/actions/stripe";
import { fetchSelf } from "app/actions/user";
import { startTrial } from "app/actions/subscription";
import { useTaxCalculation, TaxCalculationResult } from "app/hooks/useTaxCalculation";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Currency, formatMoney, formatPrice } from "app/utils/currency";

export type PropsDef = {
  disabled?: boolean;
  cardName?: string;
  cardNumber?: string;
  cardExpiry?: string;
  cardSecurityCode?: string;
  country?: string;
  usState?: string;
  city?: string;
  addressLineOne?: string;
  addressLineTwo?: string;
  postalCode?: string;
  submitTitle?: string;
  onToken?: (token?: Token) => void;
  onCancel?: () => void;
  onTaxCalculated?: (taxData: TaxCalculationResult | null) => void;
  stripe?: { [key: string]: any };
  planCode?: "coachacademy+yearly" | "coachacademy+monthly";
  couponCode?: string;
  currency?: Currency;
  isPhased?: boolean;
  // Explicit second-phase amount of the phased offer, in cents: 7500 for
  // the classic offers, 8999 for the full-price variant (nominal parity —
  // NOT the currency's standard plan price).
  phasedSecondAmount?: number;
  // Direct purchase (/purchase/academy): the checkout omits the start_trial
  // flag so the charge happens immediately, and success stays in-app instead
  // of the marketing redirect.
  purchase?: boolean;
  // Re-price the user's own Stripe subscription onto planCode (PUT
  // subscriptions/update) instead of buying a new one; the saved card pays.
  reprice?: boolean;
  // The trial paywall's buttons, from its GrowthBook flag. Each falls back to
  // today's copy; showFreePlanButton false hides the free plan button.
  ctaText?: string;
  showFreePlanButton?: boolean;
  freePlanText?: string;
};

type ErrorModel = {
  cardNameError?: MessageDescriptor | null;
  cardNumberError?: string | null;
  cardExpiryError?: string | null;
  cardSecurityCodeError?: string | null;
  countryError?: string;
  usStateError?: string;
  cityError?: string;
  addressLineOneError?: string;
  addressLineTwoError?: string;
  postalCodeError?: string | null;
  formError?: string | null;
};

type State = ErrorModel & {
  cardName: ElementChangeResponse | null;
  cardNumber: ElementChangeResponse | null;
  cardExpiry: ElementChangeResponse | null;
  cardSecurityCode: ElementChangeResponse | null;
  country?: ElementChangeResponse | null;
  usState?: ElementChangeResponse | null;
  city?: ElementChangeResponse | null;
  addressLineOne?: ElementChangeResponse | null;
  addressLineTwo?: ElementChangeResponse | null;
  postalCode: ElementChangeResponse | null;
};

const initError: ErrorModel = {
  cardNameError: null,
  cardNumberError: null,
  cardExpiryError: null,
  cardSecurityCodeError: null,
  countryError: null,
  usStateError: null,
  cityError: null,
  addressLineOneError: null,
  addressLineTwoError: null,
  postalCodeError: null,
  formError: null,
};

const initState: State = {
  cardName: null,
  cardNumber: null,
  cardExpiry: null,
  country: null,
  // @ts-ignore
  usState: null,
  city: null,
  cardSecurityCode: null,
  addressLineOne: null,
  addressLineTwo: null,
  postalCode: null,
};

// After an academy trial is started the user lands on the onboarding wizard's
// Welcome page (replaces the old external coachnow.com/academy-trial-success
// redirect), regardless of plan term or promo.
const ACADEMY_TRIAL_SUCCESS_PATH = "/academy/wizard";

function AcademyCheckoutForm(props: PropsDef) {
  const { t } = useTranslation();
  const history = useHistory();
  const productKind = "Academy";
  const currency = props.currency ?? "usd";
  // Academy's trial is plan-driven; forward only a real promo (no pga6 trigger).
  const couponCode = props.couponCode;
  // Base price + discount come from the resolved offer the PAGE fetched (shared
  // state.promoOffer). Guard against a resolution left over from a different code.
  const resolution = useSelector((store: RootState) => store.promoOffer);
  // Match the plan too, not just the code: on a month↔year toggle the code stays
  // but planCode changes, so a code-only check would feed the previous term's
  // baseAmount into the tax calc while resolving.
  const isCurrentOffer =
    (resolution.code || "").toUpperCase() === (couponCode || "").toUpperCase() &&
    resolution.planCode === props.planCode &&
    resolution.currency === currency;
  const offer = isCurrentOffer ? resolution.offer : null;
  const discount = offer?.discount;
  // Trial length: the offer's when a code applies, else the regional/7 fallback.
  // Backend-resolved (region-aware, carried even by a `none` offer); 7 covers
  // the transient not-yet-fresh render (the trial line is hidden while loading).
  const trialDays = (isCurrentOffer ? resolution.trialDays : null) ?? 7;
  // Fresh only when not pending AND the resolution echoes the current code and
  // plan — matching the plan is what catches a term toggle (month↔year keeps the
  // code, so without it the previous term's price/copy would linger). While not
  // fresh, hide the dynamic price line rather than show stale/default data.
  const loading =
    resolution.pending ||
    (resolution.code || "").toUpperCase() !== (couponCode || "").toUpperCase() ||
    resolution.planCode !== props.planCode ||
    resolution.currency !== currency;
  // The preview failed (the page offers a retry): there is no price to show
  const offerError = isCurrentOffer && resolution.error;
  const [isReady, setIsReady] = useState(false);
  // A logged-in coach who arrived via a trial/purchase link may already have a
  // card on file — offer it as a payment option instead of the billing form.
  const { paymentInfo, hasSavedCard, useNewCard, setUseNewCard, paymentInfoLoaded } = useSavedCard();
  const [pending, startPending, stopPending] = usePending();
  const [errors, addErrors, clearErrors] = useError(initError);
  const [state, setState] = useState(() =>
    mergePropsWithState(props, initState),
  );
  const { taxData, error: taxError, calculateTax } = useTaxCalculation();
  const taxCalculationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);

  const actions = useActions({
    purchaseSubscription,
    updateSubscription,
    startTrial,
    fetchSelf,
  });

  const formClass = cls(styles.stripeCardForm, isReady ? "" : "hidden");
  const disabled = props.disabled || pending.status;
  const planAmount = (isCurrentOffer ? resolution.baseAmount : 0) || 0;
  // Phased amounts come straight from the resolved schedule: first phase billed
  // for 12 months, then the last phase forever.
  const phases = offer?.phased?.phases ?? [];
  const phasedFirstAmount = phases[0]?.amount;
  const phasedSecondAmount = phases.length
    ? phases[phases.length - 1].amount
    : undefined;

  // The first invoice is charged at the discounted price when a promo applies,
  // so tax must be calculated on this amount — not the full plan price. For a
  // phased offer the first invoice is its intro phase. ACADEMYGGNASDY's $200 off
  // is just an amountOff here (no special case).
  const taxableAmount = useMemo(() => {
    if (props.isPhased) {
      return phasedFirstAmount ?? 0;
    }
    return getDiscountedAmount(planAmount, discount);
  }, [planAmount, discount, props.isPhased, phasedFirstAmount]);

  // Price message shown above the button. It mirrors the PriceCard wording: the
  // real (discounted) price plus "per first {interval}" for a temporary discount
  // or "per {interval}" for full-price / forever discounts. The phased monthly
  // offer keeps its own two-step copy.
  const priceMessage = useMemo(() => {
    // Resolving (or mid term-switch): hide the price line entirely rather than
    // flash the previous term's amount / the 7-day default.
    if (loading || offerError) {
      return null;
    }
    if (props.isPhased) {
      // Guard the phased render on the resolved schedule — don't price a phased
      // offer from an empty/loading offer.
      if (phasedFirstAmount == null || phasedSecondAmount == null) {
        return t("Applying your offer…");
      }
      const second = formatPrice(phasedSecondAmount, currency);
      // Tax is calculated on the intro first charge; show it inline once known.
      // The second phase is taxed at the same rate, so its tax scales from the
      // first phase's amount.
      if (taxData?.total && taxData?.taxAmount) {
        const secondTax = Math.round(
          (phasedSecondAmount * taxData.taxAmount) / phasedFirstAmount,
        );
        const values = {
          trialDays,
          firstWithTax: formatMoney(taxData.total, currency),
          taxAmount: formatMoney(taxData.taxAmount, currency),
          secondWithTax: formatMoney(phasedSecondAmount + secondTax, currency),
          secondTaxAmount: formatMoney(secondTax, currency),
        };
        return props.purchase
          ? t("{{firstWithTax}} per month for 12 months (incl. {{taxAmount}} tax), then {{secondWithTax}} per month (incl. {{secondTaxAmount}} tax)", values)
          : t("{{trialDays}} days free, then {{firstWithTax}} per month for 12 months (incl. {{taxAmount}} tax), then {{secondWithTax}} per month (incl. {{secondTaxAmount}} tax)", values);
      }
      const values = { trialDays, first: formatPrice(phasedFirstAmount, currency), second };
      return props.purchase
        ? t("{{first}} per month for 12 months, then {{second}} per month", values)
        : t("{{trialDays}} days free, then {{first}} per month for 12 months, then {{second}} per month", values);
    }
    const isYearly = (props?.planCode ?? "").endsWith("+yearly");
    const interval = t(isYearly ? "year" : "month");
    const billingText = t(isYearly ? "billed annually" : "billed monthly");
    const usesFirst =
      taxableAmount < planAmount && discount?.duration !== "forever";

    // Show tax-inclusive message if tax data is available
    if (taxData?.total && taxData?.taxAmount) {
      const values = {
        trialDays,
        totalWithTax: formatMoney(taxData.total, currency),
        taxAmount: formatMoney(taxData.taxAmount, currency),
        interval,
        billingText,
      };
      if (props.purchase) {
        return usesFirst
          ? t("{{totalWithTax}} per first {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values)
          : t("{{totalWithTax}} per {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values);
      }
      return usesFirst
        ? t("{{trialDays}} days free, then {{totalWithTax}} per first {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values)
        : t("{{trialDays}} days free, then {{totalWithTax}} per {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values);
    }

    const values = { trialDays, basePrice: formatPrice(taxableAmount, currency), interval, billingText };
    if (props.purchase) {
      return usesFirst
        ? t("{{basePrice}} per first {{interval}} / {{billingText}}", values)
        : t("{{basePrice}} per {{interval}} / {{billingText}}", values);
    }
    return usesFirst
      ? t("{{trialDays}} days free, then {{basePrice}} per first {{interval}} / {{billingText}}", values)
      : t("{{trialDays}} days free, then {{basePrice}} per {{interval}} / {{billingText}}", values);
  }, [loading, offerError, taxData, props?.planCode, props.isPhased, phasedFirstAmount, phasedSecondAmount, discount, planAmount, taxableAmount, currency, t, trialDays, props.purchase]);

  // On success we route to the onboarding wizard, which unmounts this form;
  // guard async state updates that run afterwards.
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Notify parent of tax calculation changes
  useEffect(() => {
    if (props.onTaxCalculated) {
      props.onTaxCalculated(taxData);
    }
  }, [taxData, props.onTaxCalculated]);

  // Immediate tax calculation on country, state, or plan change
  useEffect(() => {
    const country = state.country?.value as string | undefined;
    const postalCode = state.postalCode?.value as string | undefined;
    const usState = state.usState?.value as string | undefined;
    const city = state.city?.value as string | undefined;

    if (planAmount && props.planCode && country && postalCode) {
      calculateTax({
        amount: taxableAmount,
        priceId: props.planCode,
        country,
        state: usState,
        postalCode,
        city,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.country?.value, state.usState?.value, taxableAmount, props.planCode]);

  // Debounced tax calculation on postal code or city change (1.5 second delay)
  useEffect(() => {
    // Clear any existing timer
    if (taxCalculationTimerRef.current) {
      clearTimeout(taxCalculationTimerRef.current);
    }

    // Set new timer for 1.5 seconds
    taxCalculationTimerRef.current = setTimeout(() => {
      const country = state.country?.value as string | undefined;
      const postalCode = state.postalCode?.value as string | undefined;
      const usState = state.usState?.value as string | undefined;
      const city = state.city?.value as string | undefined;

      if (planAmount && props.planCode && country && postalCode) {
        calculateTax({
          amount: taxableAmount,
          priceId: props.planCode,
          country,
          state: usState,
          postalCode,
          city,
        });
      }
    }, 1500);

    // Cleanup function
    return () => {
      if (taxCalculationTimerRef.current) {
        clearTimeout(taxCalculationTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.postalCode?.value, state.city?.value]);

  const onStateChange = (name: string, el: ElementChangeResponse) => {
    clearErrors();
    setState({ ...state, [name]: el });
  };

  const handlePostalCodeBlur = (el: ElementChangeResponse) => {
    onStateChange("postalCode", el);
    // Clear any pending debounced calculation
    if (taxCalculationTimerRef.current) {
      clearTimeout(taxCalculationTimerRef.current);
      taxCalculationTimerRef.current = null;
    }
    // Trigger immediate calculation on blur
    const country = state.country?.value as string | undefined;
    const postalCode = el.value as string | undefined;
    const usState = state.usState?.value as string | undefined;
    const city = state.city?.value as string | undefined;

    if (planAmount && props.planCode && country && postalCode) {
      calculateTax({
        amount: taxableAmount,
        priceId: props.planCode,
        country,
        state: usState,
        postalCode,
        city,
      });
    }
  };

  const handleCityBlur = (el: ElementChangeResponse) => {
    onStateChange("city", el);
    // Clear any pending debounced calculation
    if (taxCalculationTimerRef.current) {
      clearTimeout(taxCalculationTimerRef.current);
      taxCalculationTimerRef.current = null;
    }
    // Trigger immediate calculation on blur
    const country = state.country?.value as string | undefined;
    const postalCode = state.postalCode?.value as string | undefined;
    const usState = state.usState?.value as string | undefined;
    const city = el.value as string | undefined;

    if (planAmount && props.planCode && country && postalCode) {
      calculateTax({
        amount: taxableAmount,
        priceId: props.planCode,
        country,
        state: usState,
        postalCode,
        city,
      });
    }
  };

  const handlePurchaseFreeTrial = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!props.stripe) {
      console.error("Stripe is not initialized!");
      return;
    }

    // Validate tax calculation if plan is specified
    if (planAmount && props.planCode) {
      if (!taxData || taxError) {
        alert(t("Please provide a valid billing address"));
        return;
      }
    }

    const { cardNumber, cardExpiry, cardSecurityCode } = state;

    // trim cardName postalCode to remove extra spaces
    const cardName =
      state.cardName && state.cardName.value
        ? (state.cardName.value as string).trim()
        : null;
    const country =
      state.country && state.country.value
        ? (state.country.value as string)
        : null;
    const usState =
      state.usState && state.usState.value
        ? (state.usState.value as string)
        : null;
    const city =
      state.city && state.city.value
        ? (state.city.value as string).trimLeft()
        : null;
    const addressLineOne =
      state.addressLineOne && state.addressLineOne.value
        ? (state.addressLineOne.value as string).trimLeft()
        : null;
    const addressLineTwo =
      state.addressLineTwo && state.addressLineTwo.value
        ? (state.addressLineTwo.value as string).trimLeft()
        : null;
    const postalCode =
      state.postalCode && state.postalCode.value
        ? (state.postalCode.value as string).trim()
        : null;

    // validate input for errors
    const errors = validate(
      cardName,
      cardNumber,
      cardExpiry,
      cardSecurityCode,
      country,
      usState,
      city,
      addressLineOne,
      // addressLineTwo,
      postalCode,
      t,
    );

    if (Object.keys(errors).length) {
      addErrors(errors);
      return;
    }

    startPending();

    try {
      const token = await props.stripe.createToken({
        name: cardName,
        address_country: country,
        address_city: city,
        address_state: usState || "",
        address_line1: addressLineOne,
        address_line2: addressLineTwo || "",
        address_zip: postalCode,
      });

      if (token.error) {
        let error: ErrorModel = { formError: token.error.message };
        switch (token.error.code) {
          case "incomplete_zip":
            error = { postalCodeError: token.error.message };
            break;
          case "card_declined":
            error = { cardNumberError: token.error.message };
            break;
          case "expired_card":
            error = { cardExpiryError: token.error.message };
            break;
          default:
            error = { formError: token.error.message };
        }
        addErrors(error);
        return;
      }

      const respToken = token?.token?.id;
      if (!respToken) {
        console.error("Token ID is missing!");
        return;
      }

      const purchased = await purchasePlan(respToken);
      if (!purchased) {
        return;
      }

      if (props.onToken) {
        props.onToken(token.token);
      }

      if (!props.purchase) {
        // Trial start → onboarding wizard. A direct purchase gets there too,
        // but via the page's onToken/onDone above, which tags the navigation
        // with its own "upgrade" entry point.
        history.push(ACADEMY_TRIAL_SUCCESS_PATH, {
          wizardEntryPoint: "trial_signup",
        });
      }
    } catch (err) {
      console.error(
        "Error creating token:",
        err instanceof Error ? err.message : err,
      );
    } finally {
      if (isMounted.current) {
        stopPending();
      }
    }
  };

  // Returns whether the purchase succeeded; a failure surfaces the error to
  // the user and must not fall through to the success path. `savedCard` relaxes
  // the token guard so the handler can pass the saved card's id (or "" when the
  // backend exposes none) — not a `tok_…` value, so the checkout skips the card
  // update and subscribes on the customer's existing default payment method.
  const purchasePlan = async (
    token: string,
    savedCard = false,
  ): Promise<boolean> => {
    try {
      if (!token && !savedCard) {
        throw new Error("Token is required to purchase a plan.");
      }

      // A re-price moves the existing subscription onto the academy plan, the
      // saved card paying for the new period; the academy is provisioned
      // server-side the way a purchase is. The account is refreshed for the
      // wizard's plan gate, as below.
      if (props.reprice) {
        const updated: any = await actions.updateSubscription(props?.planCode, couponCode || null);
        if (updated.type === "@stripe.post.success") {
          try {
            await actions.fetchSelf(true);
          } catch (error) {
            console.error("Error refreshing account after purchase:", error);
          }
          return true;
        }
        addErrors({
          formError:
            updated?.payload?.defaultMessage ||
            t("Something went wrong. Please try again later"),
        });
        return false;
      }

      const resp: any = await actions.purchaseSubscription({
        token,
        couponCode,
        productKind,
        planCode: props?.planCode,
        currency,
        // The trial is an explicit opt-in (same contract as analyze) — a
        // direct purchase omits the flag, so the charge happens immediately.
        ...(props.purchase ? {} : { startTrial: true }),
      });

      if (resp.type === "@stripe.post.success") {
        // The wizard and /academy/setup are gated on the Academy plan, which
        // the purchase just changed: refresh the account before navigating.
        // Forced, since fetchSelf is otherwise a no-op once loaded. Should
        // it fail, the wizard guard retries once for an arrival from
        // checkout (the entry point in router state) before judging.
        try {
          await actions.fetchSelf(true);
        } catch (error) {
          console.error("Error refreshing account after purchase:", error);
        }
        return true;
      }

      // The error action carries a MessageDescriptor (see stripe.ts ERROR_MAP).
      addErrors({
        formError:
          resp?.payload?.defaultMessage ||
          t("Something went wrong. Please try again later"),
      });
      return false;
    } catch (err) {
      console.error(
        "Error purchasing plan:",
        err instanceof Error ? err.message : err,
      );
      addErrors({
        formError: t("Something went wrong. Please try again later"),
      });
      return false;
    }
  };

  // const purchase = (token: string) => {
  //   // get coupon code if existst
  //   const coupon = actionState.coupon.data;
  //   const couponCode = coupon && coupon.couponCode ? coupon.couponCode : undefined;
  //
  //   // send purchase to api
  //   actions.purchaseSubscription({
  //     token, couponCode,
  //     productKind: activePlan.productKind,
  //     planCode: activePlan.id
  //   }).then((result: any) => {
  //     if (result.type === '@stripe.post.success') {
  //       props.onSuccess(activePlan.id);
  //     }
  //   });
  // };

  const handleKeyboard = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.which === 13) {
      e.preventDefault();
      handlePurchaseFreeTrial(e);
    }
  };

  const handleTrialClicked = async () => {
    try {
      // await actions.startTrial();
      (window as any).location = "/";
    } catch (err) {
      console.error(
        "Error when starting trial",
        err instanceof Error ? err?.message : err,
      );
    }
  };

  // Purchase/trial on the saved card — no token created, no billing address
  // entered. The backend reuses the customer's payment method and computes tax
  // from their saved address (automatic_tax), same as the /upgrade flow.
  const handlePurchaseWithSavedCard = async () => {
    if (!paymentInfo?.cardLast4) {
      return;
    }

    startPending();
    try {
      const purchased = await purchasePlan(paymentInfo.cardId ?? "", true);
      if (!purchased) {
        return;
      }

      if (props.purchase) {
        // Hand success back to the page (the token arg is unused by onDone),
        // which routes on to the onboarding wizard.
        props.onToken?.();
      } else {
        history.push(ACADEMY_TRIAL_SUCCESS_PATH, {
          wizardEntryPoint: "trial_signup",
        });
      }
    } finally {
      if (isMounted.current) {
        stopPending();
      }
    }
  };

  // Wait for the saved-card lookup before choosing the form vs. saved-card view.
  if (!paymentInfoLoaded) {
    return <Spinner />;
  }

  // Coach already has a card on file: skip the billing form, let them pay on the
  // saved card, with the option to enter a different one.
  // A re-price pays with the card on file: without one there is nothing to
  // charge, and a card is added from the billing settings, not here.
  if (props.reprice && !hasSavedCard) {
    return (
      <div className={styles.rcStripeForm}>
        <p className={styles.small_description}>
          {t("To switch plans, add a card in your billing settings first.")}
        </p>
      </div>
    );
  }

  if (hasSavedCard && !useNewCard) {
    return (
      <div className={cls(styles.rcStripeForm, disabled ? "disable" : "")}>
        <PaymentMethodSelector
          paymentInfo={paymentInfo}
          useSavedCard={!useNewCard}
          onChange={() => setUseNewCard(!useNewCard)}
          disabled={disabled || props.reprice}
        />
        <FormItem error={errors.formError}>
          <div className={styles.buttons_wrapper}>
            <div className={styles.price_description_wrapper}>
              <p className={styles.small_description}>{priceMessage}</p>
            </div>
            <ButtonVariant
              title={
                pending?.status
                  ? t("Please wait...")
                  : props.reprice
                  ? t("Purchase")
                  : props.purchase
                  ? t("Subscribe")
                  : props.ctaText ?? t("Start Free Trial")
              }
              buttonType="primary"
              onClickFunc={handlePurchaseWithSavedCard}
              disabled={pending?.status || (props.reprice && (loading || offerError))}
            />
            {!props.reprice && props.showFreePlanButton !== false && (
              <ButtonVariant
                title={props.freePlanText ?? t("Continue with Free Plan")}
                buttonType="secondary-outline"
                onClickFunc={handleTrialClicked}
                disabled={pending?.status}
              />
            )}
          </div>
        </FormItem>
      </div>
    );
  }

  return (
    <div className={cls(styles.rcStripeForm, disabled ? "disable" : "")}>
      {!isReady && <Spinner />}

      {hasSavedCard && (
        <PaymentMethodSelector
          paymentInfo={paymentInfo}
          useSavedCard={!useNewCard}
          onChange={() => setUseNewCard(!useNewCard)}
          disabled={disabled}
        />
      )}

      <form
        className={formClass}
        onKeyDown={handleKeyboard}
        onSubmit={handlePurchaseFreeTrial}
        target="_self"
      >
        <CardName
          onChange={(el: ElementChangeResponse) =>
            onStateChange("cardName", el)
          }
          value={props.cardName}
          error={errors.cardNameError}
          disabled={disabled}
        />
        <CardNumber
          onReady={() => setIsReady(true)}
          onChange={(el: ElementChangeResponse) =>
            onStateChange("cardNumber", el)
          }
          value={props.cardNumber}
          error={errors.cardNumberError}
          disabled={disabled}
        />
        <div className={styles.card_details_wrapper}>
          <CardExpiry
            onChange={(el: ElementChangeResponse) =>
              onStateChange("cardExpiry", el)
            }
            value={props.cardExpiry}
            error={errors.cardExpiryError}
            disabled={disabled}
          />
          <CardCVC
            onChange={(el: ElementChangeResponse) =>
              onStateChange("cardSecurityCode", el)
            }
            value={props.cardSecurityCode}
            error={errors.cardSecurityCodeError}
            disabled={disabled}
          />
        </div>
        <Country
          onChange={(el: ElementChangeResponse) => onStateChange("country", el)}
          value={props.country}
          error={errors.countryError}
          disabled={disabled}
        />
        {state.country &&
          state.country.value &&
          state.country.value === "US" && (
            <UsState
              onChange={(el: ElementChangeResponse) =>
                onStateChange("usState", el)
              }
              value={props.usState}
              error={errors.usStateError}
              disabled={disabled}
            />
          )}
        <PostalCode
          onChange={(el: ElementChangeResponse) =>
            onStateChange("postalCode", el)
          }
          onBlur={handlePostalCodeBlur}
          value={props.postalCode}
          error={errors.postalCodeError}
          disabled={disabled}
        />
        <City
          onChange={(el: ElementChangeResponse) => onStateChange("city", el)}
          onBlur={handleCityBlur}
          value={props.city}
          error={errors.cityError}
          disabled={disabled}
        />
        <AddressLineOne
          onChange={(el: ElementChangeResponse) =>
            onStateChange("addressLineOne", el)
          }
          value={props.addressLineOne}
          error={errors.addressLineOneError}
          disabled={disabled}
        />
        <AddressLinetwo
          onChange={(el: ElementChangeResponse) =>
            onStateChange("addressLineTwo", el)
          }
          value={props.addressLineTwo}
          // error={errors.addressLineTwoError}
          disabled={disabled}
        />
        <FormItem error={errors.formError}>
          <div className={styles.buttons_wrapper}>
            <div className={styles.price_description_wrapper}>
              <p className={styles.small_description}>
                {priceMessage}
              </p>
            </div>
            <ButtonVariant
              title={
                pending?.status
                  ? t("Please wait...")
                  : props.purchase
                  ? t("Subscribe")
                  : props.ctaText ?? t("Start Free Trial")
              }
              buttonType="primary"
              onClickFunc={handlePurchaseFreeTrial}
              disabled={pending?.status}
            />
            {props.showFreePlanButton !== false && (
              <ButtonVariant
                title={props.freePlanText ?? t("Continue with Free Plan")}
                // onClick={cancelUpgrade}
                buttonType="secondary-outline"
                onClickFunc={handleTrialClicked}
                disabled={pending?.status}
              />
            )}
          </div>
        </FormItem>
      </form>
    </div>
  );
}

export default injectStripe(AcademyCheckoutForm);

const mergePropsWithState = (props: PropsDef, state: State): State => {
  return Object.assign({}, state, {
    cardName: props.cardName,
    cardNumber: props.cardNumber,
    cardExpiry: props.cardExpiry,
    cardSecurityCode: props.cardSecurityCode,
    country: props.country,
    usState: props.usState,
    city: props.city,
    addressLineOne: props.addressLineOne,
    addresslineTwo: props.addressLineTwo,
    postalCode: props.postalCode,
  });
};

const validate = (
  cardName: string | null,
  cardNumber: ElementChangeResponse | null,
  cardExpiry: ElementChangeResponse | null,
  cardSecurityCode: ElementChangeResponse | null,
  country: string | null,
  usState: string | null,
  city: string | null,
  addressLineOne: string | null,
  // addressLineTwo: string | null,
  postalCode: string | null,
  t: any,
) => {
  const errors: ErrorModel = {};

  if (!(cardName && cardName.length)) {
    errors.cardNameError = {
      id: "error.required.card-name",
      defaultMessage: t(
        "Please enter the name that appears on your credit card.",
      ),
    };
  }

  if (!cardNumber) {
    errors.cardNumberError = t("Please enter your credit card number.");
  }

  if (!cardExpiry) {
    errors.cardExpiryError = t("Please enter your cards expiration date.");
  }

  if (!cardSecurityCode) {
    errors.cardSecurityCodeError = t(
      "Please enter the security code that appears on your card.",
    );
  }

  if (!country) {
    errors.countryError = t("Please select a country.");
  }

  if (country && country === "US" && !usState) {
    errors.usStateError = t("Please select state.");
  }

  if (!city) {
    errors.cityError = t("Please enter a valid city.");
  }

  if (!addressLineOne) {
    errors.addressLineOneError = t("Please enter an address.");
  }

  // if (!addressLineTwo) {
  //   errors.addressLineTwoError = formatMessage(
  //     LOCALES.error_required_address_line_two
  //   );
  // }

  if (!postalCode) {
    errors.postalCodeError = t("A postal code is required.");
  }
  return errors;
};
